import { IcStatistics } from '@assets/icons/40x40';
import { VStack } from '@chakra-ui/react';
import { SensorOrSubSensorInfoWithoutSection } from '@components/cockpit/panels/monitoring/monitoring.types';
import DataSheetCardLink from '@components/common/layout/DataSheetCardLink';
import SpecificMaintenanceActions from '@components/map/popup/infos/sensors/maintenance/actions/SpecificMaintenanceActions';
import WebRelayMaintenanceActions from '@components/map/popup/infos/sensors/maintenance/actions/WebRelayMaintenanceActions';
import SpecificMaintenanceLinks from '@components/map/popup/infos/sensors/maintenance/links/SpecificMaintenanceLinks';
import { useAppSelector } from '@hooks/redux.hooks';
import { useWithDispatch } from '@hooks/useWithDispatch';
import { hasSensorRoleFromActiveProfile } from '@redux/authent/authent.selectors';
import { updatePopupByMapId } from '@redux/maps/maps.reducer';
import { selectAllMonitoringEquipmentsBySensorReference } from '@redux/situation/monitoring.selectors';
import { selectSituationTime } from '@redux/situation/situation.selectors';
import { sensorFamilyFromSensorType } from '@utils/sensors/configuration.constants';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { ActionEnum } from '@/types/commons/commons.types';
import { NotificationTypeEnum } from '@/types/data/data.types';
import { MapIdEnum } from '@/types/map.types';
import { PopupTypeEnum } from '@/types/popup.types';
import { EquipmentTypeEnum } from '@/types/sensor/sensors';

import EmergencySensorPhoneNumber from '../EmergencySensorPhoneNumber';
import CommonMaintenanceActions from './CommonMaintenanceActions';

type MaintenanceProps = {
  sensorOrSubSensorInfo: SensorOrSubSensorInfoWithoutSection;
};

type popupProps =
  | {
      position: { x: number; y: number };
      mapId: MapIdEnum;
    }
  | {
      position?: never;
      mapId?: never;
    };

export default function Maintenance({
  sensorOrSubSensorInfo,
  position,
  mapId,
}: Readonly<MaintenanceProps & popupProps>) {
  const { formatMessage } = useIntl();

  const updatePopup = useWithDispatch(updatePopupByMapId);
  const replayMode = mapId === MapIdEnum.REPLAY;
  const situationTime = useAppSelector((state) => selectSituationTime(state, replayMode));
  const isPopup = position !== undefined;

  const { configuration } = sensorOrSubSensorInfo;
  const webRelaySelectorParams = useMemo(
    () => ({
      sensorAppCode: configuration.appCode,
      sensorCode: configuration.code,
      equipmentType: EquipmentTypeEnum.WEB_RELAY,
    }),
    [configuration.appCode, configuration.code],
  );

  const sensorWebRelays = useAppSelector((state) =>
    selectAllMonitoringEquipmentsBySensorReference(state, replayMode, webRelaySelectorParams),
  );

  const isSubSensor = 'parentUniqueKey' in configuration;

  const sensorAction = { action: ActionEnum.MAINTAIN, sensorFamily: sensorFamilyFromSensorType[configuration.type] };
  const canDoSensorMaintenance = useAppSelector((state) => hasSensorRoleFromActiveProfile(state, sensorAction));

  if (!canDoSensorMaintenance) {
    return null;
  }

  function handlePopup() {
    isPopup &&
      updatePopup({
        mapId,
        popup: {
          type: PopupTypeEnum.HISTORY,
          data: {
            identifiers: [configuration.uniqueKey],
            sensorType: configuration.type,
            sensorName: configuration.name,
            sensorUniqueKeys: { sensorUniqueKey: configuration.uniqueKey },
            type: NotificationTypeEnum.SENSOR,
            openingDate: situationTime ? new Date(situationTime) : new Date(),
          },
          position,
        },
      });
  }

  return (
    <VStack gap={0.25} width="100%" height="100%" textStyle="screenStd">
      <SpecificMaintenanceActions sensorOrSubSensorInfo={sensorOrSubSensorInfo} replayMode={replayMode} />
      <WebRelayMaintenanceActions monitoringWebRelays={sensorWebRelays} />
      <CommonMaintenanceActions sensorOrSubSensorInfo={sensorOrSubSensorInfo} replayMode={replayMode} />
      {!isSubSensor && isPopup && (
        <DataSheetCardLink
          label={formatMessage({ id: 'contextmenu.actions.sensorHistory' })}
          icon={IcStatistics}
          onClick={handlePopup}
        />
      )}
      {isPopup && <SpecificMaintenanceLinks sensorOrSubSensorInfo={sensorOrSubSensorInfo} />}
      <EmergencySensorPhoneNumber configuration={sensorOrSubSensorInfo.configuration} />
    </VStack>
  );
}
